import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner, Alert, Row, Col, Form } from 'react-bootstrap';
import { fetchAnalyticsStatus, fetchAnalyticsCampaigns, fetchCampaigns } from '../../services/api';

const AnalyticsTab = () => {
    const [status, setStatus] = useState(null);
    const [gaCampaigns, setGaCampaigns] = useState([]);
    const [localCampaigns, setLocalCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dias, setDias] = useState(30);

    useEffect(() => {
        loadData();
    }, [dias]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Buscar campanhas locais do banco de dados
            const localResult = await fetchCampaigns();
            if (localResult.success) {
                setLocalCampaigns(localResult.data || []);
            }

            // Verificar status da integração GA4
            const statusResult = await fetchAnalyticsStatus();
            setStatus(statusResult);

            if (statusResult.initialized) {
                // Buscar dados do GA4
                const gaResult = await fetchAnalyticsCampaigns(dias);
                if (gaResult.success) {
                    setGaCampaigns(gaResult.data || []);
                } else {
                    setError(gaResult.error || 'Erro ao buscar dados do GA4');
                }
            }
        } catch (err) {
            console.error('Erro ao carregar analytics:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Combinar dados locais com GA4
    const getCombinedCampaigns = () => {
        return localCampaigns.map(local => {
            // Procurar dados do GA4 para esta campanha
            const gaData = gaCampaigns.find(ga =>
                ga.name && ga.name.includes(local.id.toString())
            );

            return {
                ...local,
                gaData: gaData || null,
                sessions: gaData?.totalSessions || 0,
                users: gaData?.totalUsers || 0,
                pageViews: gaData?.totalPageViews || 0,
                bounceRate: gaData?.avgBounceRate || 0,
                avgDuration: gaData?.avgSessionDuration || 0
            };
        });
    };

    // Filtrar campanhas
    const getFilteredCampaigns = () => {
        const combined = getCombinedCampaigns();
        if (selectedCampaign === 'all') {
            return combined;
        }
        return combined.filter(c => c.id.toString() === selectedCampaign);
    };

    // Totais
    const getTotals = () => {
        const filtered = getFilteredCampaigns();
        return {
            campaigns: filtered.length,
            sessions: filtered.reduce((acc, c) => acc + c.sessions, 0),
            users: filtered.reduce((acc, c) => acc + c.users, 0),
            pageViews: filtered.reduce((acc, c) => acc + c.pageViews, 0)
        };
    };

    // Se não está inicializado, mostra instruções de configuração
    if (!loading && status && !status.initialized) {
        return (
            <div className="pt-3">
                <Alert variant="warning" className="mb-4">
                    <Alert.Heading>⚙️ Google Analytics não configurado</Alert.Heading>
                    <p>
                        Para ver os dados das campanhas, é necessário configurar a integração com o Google Analytics 4.
                    </p>
                    <hr />
                    <h6>Passos para configurar:</h6>
                    <ol>
                        <li>Acesse o <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a></li>
                        <li>Crie uma <strong>Service Account</strong> e baixe o arquivo JSON</li>
                        <li>Adicione a Service Account como usuário no Google Analytics (permissão "Leitor")</li>
                        <li>Envie o arquivo JSON para o servidor em: <code>/var/www/dashboard/backend/config/ga-credentials.json</code></li>
                        <li>Configure <code>GA4_PROPERTY_ID</code> no arquivo <code>.env</code></li>
                        <li>Reinicie o backend: <code>pm2 restart dashboard-backend</code></li>
                    </ol>
                    {status.error && (
                        <Alert variant="danger" className="mt-3 mb-0">
                            <strong>Erro:</strong> {status.error}
                        </Alert>
                    )}
                </Alert>
            </div>
        );
    }

    const totals = getTotals();
    const filteredCampaigns = getFilteredCampaigns();

    return (
        <div className="pt-3">
            <Row className="align-items-center mb-3">
                <Col md={4}>
                    <h5 className="mb-0 text-secondary">
                        📊 Analytics das Campanhas
                    </h5>
                    <small className="text-muted">
                        Dados de cliques e acessos via Google Analytics
                    </small>
                </Col>
                <Col md={3}>
                    <Form.Select
                        value={selectedCampaign}
                        onChange={(e) => setSelectedCampaign(e.target.value)}
                        size="sm"
                    >
                        <option value="all">📋 Todas as Campanhas</option>
                        {localCampaigns.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.nome} (ID: {c.id})
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                <Col md={3}>
                    <Form.Select
                        value={dias}
                        onChange={(e) => setDias(parseInt(e.target.value))}
                        size="sm"
                    >
                        <option value="7">Últimos 7 dias</option>
                        <option value="14">Últimos 14 dias</option>
                        <option value="30">Últimos 30 dias</option>
                        <option value="60">Últimos 60 dias</option>
                        <option value="90">Últimos 90 dias</option>
                    </Form.Select>
                </Col>
                <Col md={2} className="text-end">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={loadData}
                        disabled={loading}
                    >
                        {loading ? <Spinner animation="border" size="sm" /> : '🔄 Atualizar'}
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Carregando dados...</p>
                </div>
            ) : (
                <>
                    {/* Resumo Geral */}
                    <Row className="g-3 mb-4">
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Total de Campanhas</h6>
                                    <h3 className="text-primary fw-bold mb-0">{totals.campaigns}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Total de Sessões</h6>
                                    <h3 className="text-success fw-bold mb-0">{totals.sessions}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Usuários Únicos</h6>
                                    <h3 className="text-info fw-bold mb-0">{totals.users}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="border-0 shadow-sm text-center h-100">
                                <Card.Body>
                                    <h6 className="text-muted mb-2">Visualizações</h6>
                                    <h3 className="text-warning fw-bold mb-0">{totals.pageViews}</h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tabela de Campanhas */}
                    {filteredCampaigns.length === 0 ? (
                        <Alert variant="light" className="text-center border py-5">
                            <div className="fs-1 mb-3">📭</div>
                            <h5>Nenhuma campanha encontrada</h5>
                            <p className="text-muted">
                                Crie campanhas na aba "Campanhas Criadas" para visualizar os dados aqui.
                            </p>
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white py-3">
                                <h6 className="mb-0">
                                    {selectedCampaign === 'all' ? 'Todas as Campanhas' : 'Campanha Selecionada'}
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-3">Campanha</th>
                                            <th className="text-center">Status</th>
                                            <th className="text-center">Clientes</th>
                                            <th className="text-center">Sessões (GA4)</th>
                                            <th className="text-center">Usuários (GA4)</th>
                                            <th className="text-center">Visualizações</th>
                                            <th className="text-center">Taxa Rejeição</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredCampaigns.map((campanha, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-3">
                                                    <div className="fw-bold text-primary">
                                                        {campanha.nome}
                                                    </div>
                                                    <small className="text-muted">
                                                        ID: {campanha.id} | Criada: {new Date(campanha.criada_em).toLocaleDateString('pt-BR')}
                                                    </small>
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg={campanha.status === 'ativa' ? 'success' : 'secondary'}>
                                                        {campanha.status || 'Ativa'}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    <Badge bg="primary" className="fs-6">
                                                        {campanha.total_clientes || 0}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    {campanha.gaData ? (
                                                        <Badge bg="success" className="fs-6">
                                                            {campanha.sessions}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {campanha.gaData ? (
                                                        <Badge bg="info" className="fs-6">
                                                            {campanha.users}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {campanha.gaData ? campanha.pageViews : '-'}
                                                </td>
                                                <td className="text-center">
                                                    {campanha.gaData ? (
                                                        <span className={campanha.bounceRate > 70 ? 'text-danger' : 'text-success'}>
                                                            {campanha.bounceRate}%
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Info sobre dados do GA4 */}
                    {gaCampaigns.length === 0 && localCampaigns.length > 0 && (
                        <Alert variant="info" className="mt-3">
                            <strong>ℹ️ Dica:</strong> Os dados do Google Analytics podem demorar até 24-48h para aparecer após os cliques.
                            Os cliques são registrados instantaneamente no dashboard (coluna "Clicaram no Link" na tela da campanha).
                        </Alert>
                    )}
                </>
            )}
        </div>
    );
};

export default AnalyticsTab;
